/**
 * Axios Interceptor for API Error Handling
 * Automatically handles subscription/rate limit errors
 */

import { handleApiError, checkResponseWarnings } from './apiErrorHandler';

/**
 * Setup axios interceptors
 * @param {AxiosInstance} axiosInstance - Axios instance
 * @param {Function} toast - Toast notification function
 * @param {Function} navigate - Navigation function
 */
export const setupAxiosInterceptors = (axiosInstance, toast, navigate) => {
  // Request interceptor - auth is handled via httpOnly cookies (withCredentials: true)
  axiosInstance.interceptors.request.use(
    (config) => {
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle errors
  axiosInstance.interceptors.response.use(
    (response) => {
      // Check for warnings in response headers
      if (toast && navigate) {
        checkResponseWarnings(response, toast, navigate);
      }
      return response;
    },
    (error) => {
      // Handle errors
      if (toast && navigate) {
        const handled = handleApiError(error, toast, navigate);
        if (handled) {
          // Error was handled, don't reject
          return Promise.reject({ ...error, handled: true });
        }
      }
      return Promise.reject(error);
    }
  );
};

/**
 * Create axios instance with interceptors
 * @param {string} baseURL - API base URL
 * @param {Function} toast - Toast notification function
 * @param {Function} navigate - Navigation function
 * @returns {AxiosInstance}
 */
export const createApiClient = (baseURL, toast, navigate) => {
  const axios = require('axios');
  const instance = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  setupAxiosInterceptors(instance, toast, navigate);

  return instance;
};

const apiInterceptor = {
  setupAxiosInterceptors,
  createApiClient
};

export default apiInterceptor;

